import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, MessageCircle } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const Footer = () => {
  const { settings } = useSettings();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#4A0F1E] text-white" data-testid="footer">
      {/* Decorative top border */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10">

          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-4">
              {settings.logo ? (
                <img src={settings.logo} alt={settings.bakeryName} className="h-12 w-auto object-contain mb-3" />
              ) : (
                <FooterLogo name={settings.bakeryName} />
              )}
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              Baked with love, served with happiness. Thank you for being a part of our journey.
            </p>
            <div className="flex gap-3 mt-5">
              <a href="https://www.instagram.com/apnidhanibakers" target="_blank" rel="noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                data-testid="footer-instagram">
                <Instagram size={16} />
              </a>
              <a href={`https://wa.me/${(settings.whatsappNumber || '').replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                data-testid="footer-whatsapp">
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-playfair font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[['Home', '/'], ['Menu', '/menu'], ['Combos', '/combos'], ['Contact', '/contact']].map(([label, href]) => (
                <li key={href}>
                  <Link to={href} className="text-white/60 hover:text-white text-sm transition-colors">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-playfair font-semibold text-white mb-4">Categories</h4>
            <ul className="space-y-2">
              {['Cakes', 'Pastries', 'Patties', 'Pizza', 'Beverages', 'Ice Cream', 'Snacks', 'Party Items', 'Combos'].map(cat => (
                <li key={cat}>
                  <Link to={`/menu?category=${encodeURIComponent(cat)}`} className="text-white/60 hover:text-white text-sm transition-colors">{cat}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-playfair font-semibold text-white mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <a href={`tel:${settings.phone}`} className="flex items-start gap-2 text-white/60 hover:text-white text-sm transition-colors" data-testid="footer-phone">
                  <Phone size={14} className="mt-0.5 shrink-0" />
                  <span>{settings.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${settings.email}`} className="flex items-start gap-2 text-white/60 hover:text-white text-sm transition-colors" data-testid="footer-email">
                  <Mail size={14} className="mt-0.5 shrink-0" />
                  <span>{settings.email}</span>
                </a>
              </li>
              <li>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(settings.address)}`} target="_blank" rel="noreferrer"
                  className="flex items-start gap-2 text-white/60 hover:text-white text-sm transition-colors" data-testid="footer-address">
                  <MapPin size={14} className="mt-0.5 shrink-0" />
                  <span>{settings.address}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
          <p className="text-white/40 text-xs">© {year} {settings.bakeryName}. All rights reserved.</p>
          <p className="text-white/30 text-xs flex items-center gap-1">Made with <span className="text-rose-400">♥</span> in India</p>
        </div>
      </div>
    </footer>
  );
};

const FooterLogo = ({ name }) => (
  <div className="flex items-center gap-2">
    <svg width="32" height="32" viewBox="0 0 36 36" fill="none">
      <circle cx="18" cy="18" r="17" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
      <path d="M18 6 C10 10, 8 14, 8 18 C8 24, 12 28, 18 30 C24 28, 28 24, 28 18 C28 14, 26 10, 18 6Z" fill="rgba(255,255,255,0.2)"/>
      <ellipse cx="18" cy="19" rx="6" ry="4" fill="rgba(255,255,255,0.3)"/>
      <circle cx="18" cy="15" r="3" fill="white" opacity="0.6"/>
    </svg>
    <div>
      <div className="font-playfair text-white font-bold text-base leading-none">{name || 'Apni Dhani'}</div>
      <div className="text-white/40 text-[9px] uppercase tracking-[0.2em] leading-none">— BAKERY —</div>
    </div>
  </div>
);

export default Footer;
